/**
 * FlowAI-Übersicht (#1665) — Nicht-Techniker-Cockpit.
 *
 * Zeigt in menschlicher Sprache, WAS FlowAI tut (Wirkung, nicht Technik):
 * KPIs, laufende Workflows, ein Aktivitäts-Feed und eine Aufmerksamkeits-Liste.
 * Pflicht: jeder Vorgang nennt den Händler/Kunden. Read-only.
 *
 * @page /flowai/uebersicht
 * @since v2.801.0
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Card, Button, Modal } from '../../components/ui';
import FlowAIBreadcrumb from '../../components/flowai/FlowAIBreadcrumb';

const API = import.meta.env.VITE_API_URL || '';

const RANGES = [
  { key: 'heute', label: 'Heute' },
  { key: 'woche', label: 'Diese Woche' },
  { key: 'monat', label: 'Dieser Monat' },
];

const STATUS_META = {
  live: { label: 'Aktiv', tone: 'green' },
  testmodus: { label: 'Testmodus', tone: 'amber' },
  soon: { label: 'Bald', tone: 'gray' },
};

function Pill({ tone = 'gray', children }) {
  const tones = {
    green: 'bg-green-50 text-green-700 border-green-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
  };
  return <span className={`inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full border ${tones[tone] || tones.gray}`}>{children}</span>;
}

function Tile({ label, value, hint }) {
  return (
    <Card>
      <div className="text-[11px] uppercase tracking-wide text-[var(--text-tertiary,#6B7280)] mb-1">{label}</div>
      <div className="text-2xl font-semibold text-[var(--text-primary,#1A1A2E)]">{value}</div>
      {hint && <div className="text-[11px] text-[var(--text-tertiary,#6B7280)] mt-1">{hint}</div>}
    </Card>
  );
}

function formatDuration(min) {
  const m = Number(min) || 0;
  if (m < 60) return `${m} Min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h} Std ${rest} Min` : `${h} Std`;
}

function relTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const diff = Math.round((Date.now() - d.getTime()) / 60000);
  if (diff < 1) return 'gerade eben';
  if (diff < 60) return `vor ${diff} Min`;
  if (diff < 1440) return `vor ${Math.floor(diff / 60)} Std`;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

function fmtDateTime(ts) {
  if (!ts) return null;
  return new Date(ts).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ─── Fall-Detail-Overlay (#1668) ──────────────────────────────
function DetailModal({ open, onClose, refId }) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [showTech, setShowTech] = useState(false);

  useEffect(() => {
    if (!open || !refId) return;
    let cancelled = false;
    setLoading(true); setError(false); setDetail(null); setShowTech(false);
    fetch(`${API}/api/flowai/uebersicht/${encodeURIComponent(refId)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, refId]);

  const head = detail?.head || {};
  const emails = Array.isArray(detail?.emails) ? detail.emails : [];
  const timeline = Array.isArray(detail?.timeline) ? detail.timeline : [];
  const links = detail?.links || {};

  const footer = (
    <>
      {links.order && (
        <a href={links.order} target="_blank" rel="noopener noreferrer">
          <Button variant="ghost">Auftrag im OMS öffnen</Button>
        </a>
      )}
      {links.app_case && (
        <Button variant="secondary" onClick={() => { onClose(); navigate(links.app_case); }}>Fall öffnen</Button>
      )}
      <Button variant="primary" onClick={onClose}>Schließen</Button>
    </>
  );

  return (
    <Modal open={open} onClose={onClose} title="Fall-Detail" size="lg" footer={detail ? footer : undefined}>
      {loading && <div className="py-8 text-center text-[13px] text-[var(--text-tertiary,#6B7280)]">Lade Fall…</div>}
      {error && <div className="py-8 text-center text-[13px] text-red-600">Fall konnte nicht geladen werden.</div>}
      {detail && (
        <div className="space-y-5">
          {/* Kopf */}
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[16px] font-semibold text-[var(--text-primary,#1A1A2E)]">{head.merchant}</span>
              <Pill tone={head.status_color}>{head.status_label}</Pill>
            </div>
            <div className="flex items-center gap-3 mt-1 text-[12px] text-[var(--text-tertiary,#6B7280)]">
              {head.order_ref && <span className="font-mono">Auftrag {head.order_ref}</span>}
              {head.picking_ref && <span className="font-mono">Lieferschein {head.picking_ref}</span>}
            </div>
          </div>

          {/* Was war los */}
          <section>
            <div className="text-[11px] uppercase tracking-wide text-[var(--text-tertiary,#6B7280)] mb-1.5">Was war los</div>
            <div className="text-[13px] text-[var(--text-secondary,#4B5563)] leading-relaxed whitespace-pre-line">{detail.situation}</div>
          </section>

          {/* Übersprungen-Grund */}
          {detail.skip && (
            <section className="p-3 rounded-[10px] border border-amber-200 bg-amber-50">
              <div className="text-[11px] uppercase tracking-wide text-amber-700 mb-1">Nicht bearbeitet</div>
              <div className="text-[13px] text-amber-800">{detail.skip.label}</div>
            </section>
          )}

          {/* Timeline */}
          {timeline.length > 0 && (
            <section>
              <div className="text-[11px] uppercase tracking-wide text-[var(--text-tertiary,#6B7280)] mb-2">Was FlowAI gemacht hat</div>
              <ol className="space-y-0">
                {timeline.map((t, i) => (
                  <li key={i} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <span className="w-2 h-2 rounded-full bg-[#338edc] mt-1.5 shrink-0" />
                      {i < timeline.length - 1 && <span className="w-px flex-1 bg-[var(--border-subtle,#E5E7EB)]" />}
                    </div>
                    <div className="pb-4 min-w-0">
                      <div className="text-[13px] text-[var(--text-primary,#1A1A2E)]">{t.label}</div>
                      <div className="text-[11px] text-[var(--text-tertiary,#6B7280)]">{fmtDateTime(t.ts) || 'Zeitpunkt nicht gespeichert'}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {/* Versendete E-Mail(s) */}
          {emails.length > 0 && (
            <section>
              <div className="text-[11px] uppercase tracking-wide text-[var(--text-tertiary,#6B7280)] mb-2">
                {emails.length > 1 ? `Versendete E-Mails (${emails.length})` : 'Versendete E-Mail'}
              </div>
              <div className="space-y-3">
                {emails.map((e, i) => (
                  <div key={i} className="rounded-[10px] border border-[var(--border-subtle,#E5E7EB)] overflow-hidden">
                    <div className="px-3 py-2 bg-[var(--bg-inset,#F8F9FC)] border-b border-[var(--border-subtle,#E5E7EB)]">
                      <div className="flex items-center gap-2 flex-wrap">
                        {e.type && <Pill tone="blue">{e.type}</Pill>}
                        <span className="text-[11px] text-[var(--text-tertiary,#6B7280)]">An: <span className="text-[var(--text-secondary,#4B5563)]">{e.to}</span></span>
                        {e.sent_at && <span className="ml-auto text-[11px] text-[var(--text-tertiary,#6B7280)]">{fmtDateTime(e.sent_at)}</span>}
                      </div>
                      <div className="text-[13px] font-medium text-[var(--text-primary,#1A1A2E)] mt-1">{e.subject || '(kein Betreff)'}</div>
                    </div>
                    {e.body_html ? (
                      <iframe
                        title={`email-${i}`}
                        sandbox=""
                        srcDoc={e.body_html}
                        className="w-full h-[360px] bg-white"
                      />
                    ) : (
                      <div className="px-3 py-4 text-[12px] text-[var(--text-tertiary,#6B7280)] italic">Inhalt nicht gespeichert.</div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Technische Details (roher Fehler) */}
          {detail.error && (
            <section>
              <button
                onClick={() => setShowTech((v) => !v)}
                className="text-[12px] text-[var(--text-tertiary,#6B7280)] underline"
              >
                {showTech ? 'Technische Details ausblenden' : 'Technische Details anzeigen'}
              </button>
              {showTech && (
                <pre className="mt-2 p-3 rounded-[10px] border border-[var(--border-subtle,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] text-[11px] text-[var(--text-secondary,#4B5563)] whitespace-pre-wrap overflow-x-auto">{detail.error}</pre>
              )}
            </section>
          )}
        </div>
      )}
    </Modal>
  );
}

export default function FlowAIUebersicht() {
  const [range, setRange] = useState('woche');
  const [merchant, setMerchant] = useState('');
  const [query, setQuery] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailRef, setDetailRef] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ range });
      if (query.trim()) params.set('merchant', query.trim());
      const r = await fetch(`${API}/api/flowai/uebersicht?${params}`, { credentials: 'include' });
      const d = await r.json();
      setData(d);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [range, merchant]); // eslint-disable-line react-hooks/exhaustive-deps

  const kpis = data?.kpis || {};
  const workflows = Array.isArray(data?.workflows) ? data.workflows : [];
  const activity = Array.isArray(data?.activity) ? data.activity : [];
  const attention = Array.isArray(data?.attention) ? data.attention : [];

  return (
    <div className="p-6">
      <FlowAIBreadcrumb trail={[{ label: 'Übersicht' }]} />
      <PageHeader
        title="FlowAI-Übersicht"
        subtitle="Was FlowAI im Hintergrund für unsere Händler erledigt"
        actions={<Button onClick={load} variant="secondary">Aktualisieren</Button>}
      />

      {/* Filterleiste */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="inline-flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-subtle,#E5E7EB)]">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-3 py-1.5 text-[12px] font-medium rounded-[8px] transition ${range === r.key ? 'bg-white shadow-sm text-[var(--text-primary,#1A1A2E)]' : 'text-[var(--text-tertiary,#6B7280)]'}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); setMerchant(query); }}
          className="flex items-center gap-2"
        >
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Händler filtern…"
            className="px-3 py-1.5 text-[12px] rounded-[8px] border border-[var(--border-subtle,#E5E7EB)] w-48 focus:outline-none focus:border-[#338edc]"
          />
          <Button type="submit" variant="ghost">Filtern</Button>
          {merchant && (
            <button
              type="button"
              onClick={() => { setQuery(''); setMerchant(''); }}
              className="text-[12px] text-[var(--text-tertiary,#6B7280)] underline"
            >
              Zurücksetzen
            </button>
          )}
        </form>
      </div>

      {loading && !data ? (
        <div className="p-6 text-sm text-[var(--text-secondary,#4B5563)]">Lade Übersicht…</div>
      ) : !data ? (
        <div className="p-6 text-sm text-red-600">Übersicht konnte nicht geladen werden.</div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Tile label="Automatisch bearbeitet" value={kpis.auto_processed ?? 0} hint="Vorgänge ohne manuelles Zutun" />
            <Tile label="Händler benachrichtigt" value={kpis.customers_notified ?? 0} hint="per automatischer E-Mail" />
            <Tile label="Offene Fälle" value={kpis.open_cases ?? 0} hint="warten auf Klärung" />
            <Tile label="Zeit gespart (geschätzt)" value={formatDuration(kpis.time_saved_minutes)} hint="grobe Schätzung" />
          </div>

          {/* Workflows */}
          <Card title="Was FlowAI gerade übernimmt" className="mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {workflows.map((w) => {
                const meta = STATUS_META[w.status] || STATUS_META.soon;
                return (
                  <div key={w.key} className="flex items-start gap-3 p-3 rounded-[10px] border border-[var(--border-subtle,#E5E7EB)]">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[13px] font-semibold text-[var(--text-primary,#1A1A2E)]">{w.name}</span>
                        <Pill tone={meta.tone}>{meta.label}</Pill>
                      </div>
                      <div className="text-[12px] text-[var(--text-secondary,#4B5563)]">{w.description}</div>
                    </div>
                    {w.count != null && (
                      <div className="text-right shrink-0">
                        <div className="text-lg font-semibold text-[var(--text-primary,#1A1A2E)]">{w.count}</div>
                        <div className="text-[10px] text-[var(--text-tertiary,#6B7280)]">im Zeitraum</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Aktivitäts-Feed */}
            <Card title="Was zuletzt passiert ist">
              <div className="divide-y divide-[var(--border-subtle,#E5E7EB)]">
                {activity.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setDetailRef(a.id)}
                    className="w-full text-left py-2.5 px-1 -mx-1 rounded-[8px] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-semibold text-[var(--text-primary,#1A1A2E)] truncate">{a.merchant}</span>
                      {a.order_ref && <span className="text-[11px] text-[var(--text-tertiary,#6B7280)] font-mono">{a.order_ref}</span>}
                      <span className="ml-auto text-[11px] text-[var(--text-tertiary,#6B7280)] shrink-0">{relTime(a.ts)}</span>
                    </div>
                    <div className="text-[12px] text-[var(--text-secondary,#4B5563)] mt-0.5">{a.sentence}</div>
                  </button>
                ))}
                {activity.length === 0 && (
                  <div className="py-6 text-center text-[12px] text-[var(--text-tertiary,#6B7280)]">Keine Aktivität im Zeitraum</div>
                )}
              </div>
            </Card>

            {/* Aufmerksamkeit */}
            <Card title="Braucht vielleicht einen Blick">
              <div className="divide-y divide-[var(--border-subtle,#E5E7EB)]">
                {attention.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setDetailRef(a.id)}
                    className="w-full text-left py-2.5 px-1 -mx-1 rounded-[8px] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-semibold text-[var(--text-primary,#1A1A2E)] truncate">{a.merchant}</span>
                      {a.order_ref && <span className="text-[11px] text-[var(--text-tertiary,#6B7280)] font-mono">{a.order_ref}</span>}
                      <span className="ml-auto text-[11px] text-[var(--text-tertiary,#6B7280)] shrink-0">{relTime(a.ts)}</span>
                    </div>
                    <div className="text-[12px] text-[var(--text-secondary,#4B5563)] mt-0.5">{a.reason}</div>
                  </button>
                ))}
                {attention.length === 0 && (
                  <div className="py-6 text-center text-[12px] text-[var(--text-tertiary,#6B7280)]">Alles im grünen Bereich</div>
                )}
              </div>
            </Card>
          </div>
        </>
      )}

      <DetailModal open={!!detailRef} refId={detailRef} onClose={() => setDetailRef(null)} />
    </div>
  );
}
